import { useResidentContext } from "../../residents/provider/ResidentProvider";
import { useCreateFluidOutputMutation } from "../../../services/api/staticFormsApi";
import usePagination from "ui/molecules/pagination/usePagination";
import { useForm } from "react-hook-form";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useGetResidentQuery } from "../../../services/api/residentsApi";
import { FluidOutputFormFields } from "../../../services/api/types/forms/requests/FluidOutputFormRequest";
import { format } from "date-fns";
import { FluidOutputRequest } from "../../../services/api/types/forms/requests/FluidOutputRequest";
import { useFluidOuputWithOffline } from "../../offline/hooks/useFluidOuputWithOffline";

interface Props {
  onSuccess?: () => void;
}

export function useHandleFluidOutput({ onSuccess }: Props) {
  const { residentId } = useResidentContext();
  const [triggerCreateFluidOutput] = useCreateFluidOutputMutation();
  const { showNewToast, showErrorToast, showOfflineToast } = useDisplayToast();

  const { currentPage, handlePageChange } = usePagination();

  const {
    data,
    isFetching: isHistoryLoading,
    meta,
  } = useFluidOuputWithOffline({
    residentId,
    currentPage,
  });

  const { data: residentData, refetch } = useGetResidentQuery(
    { resident: residentId! },
    { skip: !residentId },
  );

  const fluidOutputTotal = residentData?.fluid_output_totals ?? 0;

  const today = new Date();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    watch,
  } = useForm<FluidOutputFormFields>({
    defaultValues: {
      residentId,
      output_volume: undefined,
      output_type: undefined,
      output_type_description: "",
      recorded_date: format(today, "yyyy-MM-dd"),
      recorded_time: format(today, "HH:mm"),
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      const {
        recorded_date: RecordedDate,
        recorded_time: RecordedTime,
        ...rest
      } = data;

      const finalDateTime = new Date(
        `${format(new Date(RecordedDate), "yyyy-MM-dd")}T${format(
          new Date(`1970-01-01T${RecordedTime}`),
          "HH:mm:ss",
        )}`,
      );

      const formattedRecordedAt = format(
        finalDateTime,
        "yyyy-MM-dd'T'HH:mm:ssXXX",
      );

      const transformedData: FluidOutputRequest = {
        ...rest,
        recorded_at: formattedRecordedAt,
        residentId: residentId!,
      };

      await triggerCreateFluidOutput(transformedData).unwrap();
      await refetch();

      showNewToast({
        title: "Fluid Output logged successfully",
        message: "The log has been created successfully",
        type: "success",
      });
    } catch (e) {
      showOfflineToast(
        {
          title: "Fluid Output saved offline",
        },
        () => showErrorToast(e),
      );
    }
    if (onSuccess) onSuccess();
  });

  return {
    data,
    handlePageChange,
    isHistoryLoading,
    control,
    isSubmitting,
    submit,
    isDirty,
    watch,
    fluidOutputTotal,
    meta,
  };
}
